import { useState } from "react";
import { subscribeNewsletter } from "../../lib/forms";
import { analytics } from "../../lib/analytics";
import { TID } from "../../lib/testIds";
import { Mail, Check, AlertCircle, Loader2 } from "lucide-react";

export function NewsletterSignup({
  compact = false,
  sourcePage = "unknown",
  heading = "Stay sharp on money decisions.",
  subheading = "One useful email per week. No spam. Unsubscribe anytime.",
  idScope = "default",
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(true);
  const [state, setState] = useState({ status: "idle", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    analytics.newsletterClick(sourcePage);
    setState({ status: "loading", message: "" });
    try {
      await subscribeNewsletter({
        email,
        first_name: firstName || null,
        source_page: sourcePage,
        marketing_consent: consent,
      });
      analytics.newsletterSubmit(sourcePage);
      setState({
        status: "success",
        message: "You're in. Check your inbox for confirmation soon.",
      });
      setEmail("");
      setFirstName("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setState({
        status: "error",
        message:
          typeof detail === "string"
            ? detail
            : "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div className={compact ? "" : "rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"}>
      {!compact && (
        <div className="mb-5">
          <p className="mml-overline">Newsletter</p>
          <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {heading}
          </h3>
          <p className="mt-2 text-slate-600">{subheading}</p>
        </div>
      )}
      {compact && (
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-slate-900">{heading}</h4>
          <p className="mt-1 text-xs text-slate-500">{subheading}</p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-3">
        {!compact && (
          <div>
            <label
              htmlFor={`${idScope}-newsletter-first-name`}
              className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >
              First name (optional)
            </label>
            <input
              id={`${idScope}-newsletter-first-name`}
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid={`${idScope}-${TID.newsletterFirstName}`}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
              placeholder="Alex"
            />
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id={`${idScope}-newsletter-email`}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={
                idScope === "footer"
                  ? TID.footerNewsletterEmail
                  : `${idScope}-${TID.newsletterEmail}`
              }
              placeholder="you@example.com"
              className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-9 pr-4 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
          <button
            type="submit"
            disabled={state.status === "loading"}
            data-testid={
              idScope === "footer"
                ? TID.footerNewsletterSubmit
                : `${idScope}-${TID.newsletterSubmit}`
            }
            className="mml-btn-primary whitespace-nowrap px-5 py-3 text-sm"
          >
            {state.status === "loading" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Subscribing…
              </>
            ) : (
              "Subscribe"
            )}
          </button>
        </div>

        {!compact && (
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid={`${idScope}-${TID.newsletterConsent}`}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>
              I agree to receive money-tips emails. I can unsubscribe at any
              time.
            </span>
          </label>
        )}

        {state.status === "success" && (
          <div
            data-testid={`${idScope}-${TID.newsletterSuccess}`}
            className="flex items-start gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          >
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{state.message}</span>
          </div>
        )}
        {state.status === "error" && (
          <div
            data-testid={`${idScope}-${TID.newsletterError}`}
            className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{state.message}</span>
          </div>
        )}
      </form>
    </div>
  );
}
